import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "src/environments/environment";
import { map } from "rxjs/operators";
import { ReplaySubject, Observable } from "rxjs";

import { UsuarioResponse } from "../models/usuarioresponse";
import { RevokeTokenResponse } from "../models/revoketokenresponse";
import { RefreshTokenResponse } from "../models/refreshtokenresponse";
import { RecuperarClaveResponse } from "../models/recuperarclaveresponse";
import { AceptaCondicionesResponse } from "../models/aceptacondicionesresponse";
import { CambiarClaveResponse } from "../models/cambiarclaveresponse";
import { FormularioSolicitudAccesoResponse } from "../models/formulariosolicitudaccesoresponse";
import { StorageService } from "./storage.service";
import { Roles } from "../models/roles";
import { RolesTypes } from "../models/rolestypes";
import { isTupleTypeNode } from "typescript";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  baseUrl = environment.URL_API;
  private currentUserSource = new ReplaySubject<UsuarioResponse>(1);
  currentUser$ = this.currentUserSource.asObservable();

  constructor(
    private http: HttpClient,
    private storageService: StorageService
  ) {}

  async isLogedIn() {
    try {
      let token = JSON.parse(localStorage.getItem("usuario"))
        ? JSON.parse(localStorage.getItem("usuario")).token
        : null;
      //console.log(token);
      if (token) {
        const httpOptions = {
          headers: new HttpHeaders({
            "Content-Type": "application/json",
            Authorization: token,
          }),
        };
        const response = await this.http
          .get<any>(this.baseUrl + "api/auth/echouser", httpOptions)
          .toPromise();
        if (response) {
          const status = response.split(":")[2];
          if (status.trim().toLowerCase() == "true") {
            return true;
          } else {
            return false;
          }
        }
      } else {
        return false;
      }
    } catch (err) {
      console.log("Error validando si se ha iniciado sesión: ", err);
      return false;
    }
  }

  async esRolPermitido(rolesUsuario: Roles[], rolesPermitidos: Roles[]) {
    for (const rol of rolesUsuario) {
      for (const rolPermitido of rolesPermitidos) {
        if (rol.toLowerCase() === rolPermitido.toLowerCase()) {
          return true;
        }
      }
    }
    return false;
  }

  async esRolTipoPermitido(rolesUsuario: Roles[], rolesPermitidos: Roles[], roleTypeActual: RolesTypes, rolesTypesPermitidos: RolesTypes[]) {
    let isRol = false;
    let isType = false;
    for (const rol of rolesUsuario) {
      for (const rolPermitido of rolesPermitidos) {
        if (rol.toLowerCase() === rolPermitido.toLowerCase()) {
          isRol = true;
        }
      }
    }

    for (const roleTypePermitido of rolesTypesPermitidos) {
      if (roleTypeActual.toString() === roleTypePermitido.toLowerCase()) {
        isType = true;
      }
    }

    return isRol && isType;
  }

  async login(model: any) {
    return this.http
      .post(this.baseUrl + "api/auth/login", model)
      .pipe(
        map(async (response: UsuarioResponse) => {
          const user = response;
          if (user) {
            await this.setCurrentUser(response);
            return user;
          }
        })
      )
      .toPromise();
  }

  refreshToken(model: any) {
    return this.http.post<RefreshTokenResponse>(
      this.baseUrl + "api/auth/refrescartoken",
      model
    );
  }

  revokeToken(model: any) {
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Authorization: model.token,
      }),
    };

    return this.http.post<RevokeTokenResponse>(
      this.baseUrl + "api/auth/revocarrefrescartoken",
      model,
      httpOptions
    );
  }

  async setCurrentUser(usuarioResponse: UsuarioResponse) {
    usuarioResponse.usuario.perfiles = [];
    const roles = this.getDecodedToken(usuarioResponse.token).role;
    usuarioResponse.usuario.idUsuario = this.getDecodedToken(
      usuarioResponse.token
    ).nameid;
    Array.isArray(roles)
      ? (usuarioResponse.usuario.perfiles = roles)
      : usuarioResponse.usuario.perfiles.push(roles);
    if (localStorage.getItem("usuario")) {
      localStorage.removeItem("usuario");
      localStorage.setItem("usuario", JSON.stringify(usuarioResponse));
    } else {
      localStorage.setItem("usuario", JSON.stringify(usuarioResponse));
    }
    this.currentUserSource.next(usuarioResponse);
  }

  logout() {
    localStorage.removeItem("usuario");
    this.storageService.removeLocalStorage("usuario");
    this.storageService.removeLocalStorage("cookie_login");
    this.currentUserSource.next(null);
  }

  getDecodedToken(token) {
    return JSON.parse(atob(token.split(".")[1]));
  }

  recuperarClave(model: any) {
    return this.http
      .post<RecuperarClaveResponse>(
        this.baseUrl + "api/auth/recuperarclave",
        model
      )
      .toPromise();
  }

  aceptaCondiciones(model: any) {
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Authorization: model.token,
      }),
    };

    return this.http
      .post<AceptaCondicionesResponse>(
        this.baseUrl + "api/auth/aceptacondiciones",
        model,
        httpOptions
      )
      .toPromise();
  }

  cambiarClave(model: any) {
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Authorization: model.token,
      }),
    };

    return this.http
      .post<CambiarClaveResponse>(
        this.baseUrl + "api/auth/cambiarclave",
        model,
        httpOptions
      )
      .toPromise();
  }

  formularioSolicitudAcceso(model: any) {
    return this.http
      .post<FormularioSolicitudAccesoResponse>(
        this.baseUrl + "api/auth/formulariosolicitudacceso",
        model
      )
      .toPromise();
  }

  formularioDeContacto(model: any, archivo?: Blob) {
    if (archivo) {
      const httpOptions = {
        headers: new HttpHeaders({
          Authorization: model.token,
        }),
      };
      let formData = new FormData();
      formData.append("jsonRequest", JSON.stringify(model.req));
      formData.append("fileRequest", archivo);

      return this.http
        .post<any>(
          this.baseUrl + "api/auth/formulariocontacto",
          formData,
          httpOptions
        )
        .toPromise();
    } else {
      const httpOptions = {
        headers: new HttpHeaders({
          Authorization: model.token,
        }),
      };
      let formData = new FormData();
      formData.append("jsonRequest", JSON.stringify(model.req));

      return this.http
        .post<any>(
          this.baseUrl + "api/auth/formulariocontacto",
          formData,
          httpOptions
        )
        .toPromise();
    }
  }
}
